import React from "react";
import type { Position, Player } from "../../../shared/models/Player";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import { BoardCell, CellContent } from "../../../shared/components/BoardCell";
import TeamLogo from "../../../shared/components/TeamLogo";
import { formatTeamAbbreviation } from "../../../shared/utils/teamAbbreviation";

function numberToWords(value: number) {
  const underTwenty = ["Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty"];
  if (value < 20) return underTwenty[value] ?? String(value);
  const tensDigit = Math.floor(value / 10);
  const onesDigit = value % 10;
  return `${tens[tensDigit] ?? String(value)}${onesDigit ? ` ${underTwenty[onesDigit]}` : ""}`;
}

export function PlannerFavoriteCard(props: {
  player: Player;
  drafted?: boolean;
  posColor: (pos: Position) => string;
  sortableEnabled?: boolean;
  hidden?: boolean;
  onToggleFavorite?: (id: string) => void;
  mobileMode?: boolean;
  tabletMode?: boolean;
}) {
  const { player, drafted = false, posColor, sortableEnabled = true, hidden = false, onToggleFavorite, mobileMode = false, tabletMode = false } = props;
  const cardWidth = mobileMode ? (tabletMode ? 91 : 72) : 140;
  const cardMinHeight = mobileMode ? (tabletMode ? 64 : 48) : undefined;
  const favoriteTop = mobileMode ? (tabletMode ? 9 : 8) : 8;
  const favoriteRight = mobileMode ? (tabletMode ? 8 : 7) : 10;
  const favoriteSize = mobileMode ? (tabletMode ? 11 : 9) : 18;
  const displayTeam = React.useMemo(() => formatTeamAbbreviation(player.team, "FA"), [player.team]);
  const nameLines = React.useMemo(() => {
    const parts = player.name.trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 1) return [parts[0] ?? "", ""];
    const suffixes = new Set(["jr", "jr.", "sr", "sr.", "ii", "iii", "iv", "v"]);
    const lastPart = parts[parts.length - 1] ?? "";
    if (parts.length >= 3 && suffixes.has(lastPart.toLowerCase())) {
      return [parts.slice(0, -2).join(" "), parts.slice(-2).join(" ")];
    }
    return [parts.slice(0, -1).join(" "), lastPart];
  }, [player.name]);

  return (
    <div
      style={{
        position: "relative",
        opacity: hidden ? 0 : drafted ? 0.62 : 1,
        filter: drafted ? "grayscale(20%)" : undefined,
        pointerEvents: hidden ? "none" : undefined,
      }}
    >
      <BoardCell
        id={`draft-planner:${player.id}`}
        drafted={drafted}
        dimWhenDrafted={false}
        showDraftedBanner={false}
        onToggleDrafted={() => {}}
        bg={posColor(player.position)}
        sortable={sortableEnabled}
        clickable={false}
        width={cardWidth}
        minWidth={cardWidth}
        minHeight={cardMinHeight}
        borderRadius={mobileMode ? 7 : 16}
        padding={mobileMode ? (tabletMode ? 5 : 4) : 8}
        marginRight={mobileMode ? 0 : 4}
      >
        {mobileMode ? (
          <div style={{ position: "relative", zIndex: 1, height: "100%" }}>
            {player.team ? (
              <div
                style={{
                  position: "absolute",
                  top: tabletMode ? 12 : 8,
                  right: tabletMode ? 1 : 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <TeamLogo
                  team={player.team}
                  size={tabletMode ? 15.625 : 14.0625}
                  fallback={<span style={{ fontSize: tabletMode ? 7 : 6, fontWeight: 700, color: "rgba(255,255,255,0.88)" }}>{displayTeam}</span>}
                />
              </div>
            ) : null}
            <div
              style={{
                marginTop: tabletMode ? 9 : 7,
                paddingRight: tabletMode ? 22 : 18,
                fontSize: tabletMode ? 8.5 : 7.5,
                lineHeight: 1,
                fontWeight: 650,
                color: "rgba(255,255,255,0.76)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                letterSpacing: -0.1,
                display: "flex",
                alignItems: "center",
              }}
            >
              <span>{player.position}</span>
              <span style={{ opacity: 0.5 }}>-</span>
              <span>{displayTeam}</span>
            </div>
            <div
              style={{
                marginTop: tabletMode ? 4 : 3,
                paddingRight: tabletMode ? 10 : 8,
                fontSize: tabletMode ? 9 : 8,
                lineHeight: tabletMode ? 1.02 : 1,
                fontWeight: 700,
                color: "rgba(255,255,255,0.97)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                gap: 0,
                minHeight: tabletMode ? 16 : 14,
                overflow: "hidden",
                letterSpacing: -0.08,
              }}
            >
              <span style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{nameLines[0]}</span>
              <span style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{nameLines[1]}</span>
            </div>
          </div>
        ) : (
          <CellContent
            label={""}
            name={player.name}
            position={player.position}
            team={player.team}
            imageUrl={player.imageUrl}
            showDash
            showImage
            forceFullName
          />
        )}
      </BoardCell>
      {drafted ? (
        <div
          style={{
            position: "absolute",
            top: mobileMode ? 4 : 6,
            right: mobileMode ? 4 : 6,
            padding: mobileMode ? "1px 6px" : "2px 8px",
            borderRadius: 999,
            fontSize: mobileMode ? (tabletMode ? 9 : 8) : 11,
            fontWeight: 900,
            letterSpacing: 0.2,
            background: "#05070f",
            color: "#fff",
            zIndex: 4,
            boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
            pointerEvents: "none",
          }}
        >
          DRAFTED
        </div>
      ) : null}
      {onToggleFavorite ? (
        <button
          type="button"
          aria-label="Unfavorite player"
          title="Unfavorite"
          onPointerDown={(event) => {
            event.stopPropagation();
          }}
          onMouseDown={(event) => {
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.stopPropagation();
            onToggleFavorite(player.id);
          }}
          style={{
            position: "absolute",
            top: favoriteTop,
            right: favoriteRight,
            border: "none",
            background: "transparent",
            color: "#fbbf24",
            fontSize: favoriteSize,
            lineHeight: 1,
            cursor: "pointer",
            zIndex: 3,
            padding: 0,
            textShadow: "0 1px 2px rgba(0,0,0,0.35)",
          }}
        >
          ★
        </button>
      ) : null}
    </div>
  );
}

export function PlannerRoundLane(props: {
  round: number;
  playerIds: string[];
  playersById: Record<string, Player>;
  draftedIds: Set<string>;
  posColor: (pos: Position) => string;
  activePlannerPlayerId: string | null;
  isCurrentRound: boolean;
  onToggleFavorite?: (id: string) => void;
  onOpenAddPlayer: (round: number) => void;
  mobileMode?: boolean;
  tabletMode?: boolean;
}) {
  const { round, playerIds, playersById, draftedIds, posColor, activePlannerPlayerId, isCurrentRound, onToggleFavorite, onOpenAddPlayer, mobileMode = false, tabletMode = false } = props;
  const { setNodeRef, isOver } = useDroppable({ id: `draft-planner-round:${round}` });
  const title = `Round ${numberToWords(round)}`;
  const cardWidth = mobileMode ? (tabletMode ? 91 : 72) : 140;
  const cardHeight = mobileMode ? (tabletMode ? 64 : 48) : 92;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: mobileMode ? (tabletMode ? 5 : 4) : 8,
        padding: mobileMode ? (tabletMode ? "5px 3px 5px 5px" : "4px 2px 4px 4px") : "8px 6px 8px 8px",
        borderTop: round === 1 ? "none" : "1px solid rgba(255,255,255,0.08)",
        background: isCurrentRound ? "rgba(255, 215, 0, 0.08)" : undefined,
        boxShadow: isCurrentRound ? "inset 0 0 0 1px rgba(255, 215, 0, 0.18)" : undefined,
        borderRadius: mobileMode ? (tabletMode ? 11 : 9) : 14,
      }}
    >
      <div
        style={{
          padding: 0,
          color: isCurrentRound ? "rgba(255, 235, 140, 0.98)" : "var(--text-0)",
          fontWeight: 900,
          fontSize: mobileMode ? (tabletMode ? 10 : 8) : 12,
          lineHeight: 1.1,
          letterSpacing: mobileMode ? 0.2 : 0.3,
          textTransform: "uppercase",
          opacity: isCurrentRound ? 1 : 0.8,
        }}
      >
        {title}
      </div>

      <div
        style={{
          minHeight: cardHeight,
          padding: mobileMode ? "2px" : "4px",
          borderRadius: mobileMode ? (tabletMode ? 11 : 9) : 16,
          display: "flex",
          flexWrap: mobileMode ? "wrap" : "nowrap",
          gap: mobileMode ? (tabletMode ? 5 : 4) : 8,
          alignItems: "flex-start",
          overflowX: mobileMode ? "hidden" : "auto",
          overflowY: mobileMode ? "visible" : "hidden",
          scrollbarWidth: "thin",
        }}
      >
        <SortableContext
          items={playerIds.map((playerId) => `draft-planner:${playerId}`)}
          strategy={horizontalListSortingStrategy}
        >
          {playerIds.map((playerId) => {
            const player = playersById[playerId];
            if (!player) return null;
            return (
              <PlannerFavoriteCard
                key={playerId}
                player={player}
                drafted={draftedIds.has(playerId)}
                posColor={posColor}
                hidden={playerId === activePlannerPlayerId}
                onToggleFavorite={onToggleFavorite}
                mobileMode={mobileMode}
                tabletMode={tabletMode}
              />
            );
          })}
        </SortableContext>
        <button
          type="button"
          ref={setNodeRef}
          onClick={() => onOpenAddPlayer(round)}
          style={{
            width: cardWidth,
            minWidth: cardWidth,
            height: cardHeight,
            boxSizing: "border-box",
            padding: mobileMode ? (tabletMode ? 5 : 4) : 8,
            position: "relative",
            overflow: "hidden",
            borderRadius: mobileMode ? 7 : 16,
            border: "none",
            background: "transparent",
            boxShadow: "none",
            color: "rgba(255,255,255,0.9)",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: mobileMode ? (tabletMode ? 24 : 20) : 32,
            fontWeight: 300,
            lineHeight: 1,
          }}
        >
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: mobileMode ? 5 : 6,
              right: 0,
              bottom: mobileMode ? 5 : 6,
              left: 0,
              borderRadius: mobileMode ? 7 : 12,
              border: "1px dashed rgba(255,255,255,0.18)",
              background: isOver ? "rgba(255,255,255,0.06)" : "transparent",
              boxShadow: isOver ? "0 0 0 2px rgba(255,255,255,0.18)" : undefined,
              pointerEvents: "none",
            }}
          />
          <span style={{ position: "relative", zIndex: 1 }}>+</span>
        </button>
      </div>
    </div>
  );
}
